import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Loader2, Plus, Edit3, Trash2, Eye, EyeOff, X, FileText, Save } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  slug: '',
  title: '',
  excerpt: '',
  content: '',
  cover_image: '',
  language: 'es',
  tags: '',
  materials: [],
  published: false,
};

export default function AdminBlog({ onSessionExpired }) {
  const { token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [allMaterials, setAllMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null); // form state or null
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` }, withCredentials: true };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const [postsRes, materialsRes] = await Promise.all([
        axios.get(`${API}/blog/admin`, authHeaders),
        axios.get(`${API}/materials`).catch(() => ({ data: [] })),
      ]);
      setPosts(postsRes.data);
      setAllMaterials(materialsRes.data || []);
    } catch (e) {
      if (e.response?.status === 401 && onSessionExpired) return onSessionExpired();
      toast.error('Error al cargar los artículos');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); /* eslint-disable-next-line */ }, []);

  const openNew = () => setEditor({ ...EMPTY, _mode: 'create' });
  const openEdit = (post) => setEditor({
    ...post,
    tags: (post.tags || []).join(', '),
    materials: post.materials || [],
    _mode: 'edit',
    _id: post.id,
  });

  const handleSave = async () => {
    if (!editor.title.trim() || !editor.content.trim() || !editor.excerpt.trim()) {
      toast.error('Título, extracto y contenido son obligatorios');
      return;
    }
    setSaving(true);
    const payload = {
      slug: editor.slug,
      title: editor.title.trim(),
      excerpt: editor.excerpt.trim(),
      content: editor.content,
      cover_image: editor.cover_image || null,
      language: editor.language,
      tags: editor.tags.split(',').map((t) => t.trim()).filter(Boolean),
      materials: editor.materials || [],
      published: editor.published,
    };
    try {
      if (editor._mode === 'create') {
        await axios.post(`${API}/blog`, payload, authHeaders);
        toast.success('Artículo creado');
      } else {
        await axios.put(`${API}/blog/${editor._id}`, payload, authHeaders);
        toast.success('Artículo actualizado');
      }
      setEditor(null);
      fetchPosts();
    } catch (e) {
      if (e.response?.status === 401 && onSessionExpired) return onSessionExpired();
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`¿Eliminar "${post.title}"? Esta acción es irreversible.`)) return;
    setDeletingId(post.id);
    try {
      await axios.delete(`${API}/blog/${post.id}`, authHeaders);
      toast.success('Artículo eliminado');
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    } finally {
      setDeletingId(null);
    }
  };

  const togglePublished = async (post) => {
    try {
      await axios.put(`${API}/blog/${post.id}`, {
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt,
        content: post.content,
        cover_image: post.cover_image || null,
        language: post.language,
        tags: post.tags || [],
        materials: post.materials || [],
        published: !post.published,
      }, authHeaders);
      toast.success(post.published ? 'Movido a borradores' : '¡Artículo publicado!');
      fetchPosts();
    } catch (e) {
      toast.error('Error al cambiar el estado');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#4A4A4A] mb-1">Blog</h2>
          <p className="text-gray-600">{posts.length} {posts.length === 1 ? 'artículo' : 'artículos'} · {posts.filter((p) => p.published).length} publicados</p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-post-btn"
          className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white rounded-xl font-semibold shadow"
        >
          <Plus className="w-4 h-4" /> Nuevo artículo
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-10 h-10 text-[#7DBDD9] animate-spin" /></div>
      ) : posts.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-2xl border border-gray-100" data-testid="no-posts">
          <FileText className="w-14 h-14 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">Aún no tienes artículos. ¡Crea el primero!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map((post) => (
            <motion.div
              key={post.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-2xl border border-gray-100 p-5 hover:shadow-md transition-shadow"
              data-testid={`post-row-${post.slug}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-bold text-[#4A4A4A] text-lg truncate">{post.title}</h3>
                    <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${post.published ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {post.published ? 'Publicado' : 'Borrador'}
                    </span>
                    <span className="px-2 py-0.5 rounded-md text-xs font-semibold bg-blue-50 text-blue-600 uppercase">
                      {post.language}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 line-clamp-2">{post.excerpt}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-400">
                    <span>{post.views || 0} lecturas</span>
                    <span>· /{post.slug}</span>
                    {(post.tags || []).slice(0, 3).map((tag) => (
                      <span key={tag} className="text-[#7DBDD9]">#{tag}</span>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => togglePublished(post)}
                    title={post.published ? 'Despublicar' : 'Publicar'}
                    data-testid={`toggle-published-${post.slug}`}
                    className={`p-2 rounded-lg ${post.published ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`}
                  >
                    {post.published ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                  </button>
                  <button
                    onClick={() => openEdit(post)}
                    title="Editar"
                    data-testid={`edit-post-${post.slug}`}
                    className="p-2 rounded-lg text-[#7DBDD9] hover:bg-[#E8F5FA]"
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(post)}
                    disabled={deletingId === post.id}
                    title="Eliminar"
                    data-testid={`delete-post-${post.slug}`}
                    className="p-2 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50"
                  >
                    {deletingId === post.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Editor modal */}
      <AnimatePresence>
        {editor && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto"
            onClick={() => !saving && setEditor(null)}
            data-testid="post-editor-modal"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-3xl p-6 max-w-3xl w-full shadow-2xl my-8"
            >
              <div className="flex justify-between items-center mb-5">
                <h3 className="text-xl font-bold text-[#4A4A4A]">
                  {editor._mode === 'create' ? 'Nuevo artículo' : 'Editar artículo'}
                </h3>
                <button onClick={() => setEditor(null)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
              </div>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Título *</label>
                    <input
                      type="text"
                      value={editor.title}
                      onChange={(e) => setEditor({ ...editor, title: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] focus:ring-2 focus:ring-[#A8D8EA]/30 outline-none"
                      placeholder="Los 10 errores más comunes al aprender español..."
                      data-testid="post-title-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Idioma</label>
                    <select
                      value={editor.language}
                      onChange={(e) => setEditor({ ...editor, language: e.target.value })}
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none bg-white"
                      data-testid="post-language-select"
                    >
                      <option value="es">Español</option>
                      <option value="en">English</option>
                      <option value="de">Deutsch</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Slug (URL) — opcional</label>
                  <input
                    type="text"
                    value={editor.slug}
                    onChange={(e) => setEditor({ ...editor, slug: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none"
                    placeholder="se-generará-automaticamente-del-titulo"
                    data-testid="post-slug-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Extracto * <span className="text-xs text-gray-400">(160 caracteres — aparece en la lista y en Google)</span></label>
                  <textarea
                    value={editor.excerpt}
                    onChange={(e) => setEditor({ ...editor, excerpt: e.target.value })}
                    maxLength={200}
                    rows={2}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none"
                    placeholder="Un resumen breve y atractivo del artículo..."
                    data-testid="post-excerpt-input"
                  />
                  <p className="text-xs text-gray-400 mt-1 text-right">{editor.excerpt.length} / 200</p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Imagen de portada — opcional</label>
                  <input
                    type="url"
                    value={editor.cover_image || ''}
                    onChange={(e) => setEditor({ ...editor, cover_image: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none"
                    placeholder="https://ejemplo.com/imagen.jpg"
                    data-testid="post-cover-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Contenido * <span className="text-xs text-gray-400">(admite Markdown: **negrita**, *cursiva*, # títulos, [enlace](url), listas...)</span></label>
                  <textarea
                    value={editor.content}
                    onChange={(e) => setEditor({ ...editor, content: e.target.value })}
                    rows={14}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none font-mono text-sm"
                    placeholder="# Introducción&#10;&#10;Aquí escribes el artículo con **markdown**...&#10;&#10;## Un subtítulo&#10;&#10;- Punto 1&#10;- Punto 2"
                    data-testid="post-content-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Etiquetas — separadas por comas</label>
                  <input
                    type="text"
                    value={editor.tags}
                    onChange={(e) => setEditor({ ...editor, tags: e.target.value })}
                    className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#A8D8EA] outline-none"
                    placeholder="gramática, principiantes, DELE"
                    data-testid="post-tags-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-[#4A4A4A] mb-1">Materiales descargables — se mostrarán al final del artículo con captura de email</label>
                  {allMaterials.length === 0 ? (
                    <p className="text-xs text-gray-400 italic mt-1">No hay materiales subidos aún. Ve a la pestaña "Materiales" para subir PDFs.</p>
                  ) : (
                    <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-xl p-3 space-y-2 bg-gray-50">
                      {allMaterials.map((m) => (
                        <label key={m.id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-white rounded p-1.5">
                          <input
                            type="checkbox"
                            checked={(editor.materials || []).includes(m.id)}
                            onChange={(e) => {
                              const current = editor.materials || [];
                              const next = e.target.checked
                                ? [...current, m.id]
                                : current.filter((id) => id !== m.id);
                              setEditor({ ...editor, materials: next });
                            }}
                            data-testid={`post-material-${m.id}`}
                            className="w-4 h-4 accent-[#7DBDD9]"
                          />
                          <span className="text-[#4A4A4A]">{m.title}</span>
                          <span className="text-xs text-gray-400 ml-auto">{m.downloads || 0} desc.</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                <label className="flex items-center gap-3 cursor-pointer" data-testid="post-publish-toggle">
                  <input
                    type="checkbox"
                    checked={editor.published}
                    onChange={(e) => setEditor({ ...editor, published: e.target.checked })}
                    className="w-5 h-5 accent-[#7DBDD9]"
                  />
                  <span className="text-sm text-[#4A4A4A]">
                    <strong>Publicar</strong> — hacer visible en el blog público
                  </span>
                </label>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setEditor(null)}
                    className="px-6 py-2.5 rounded-xl bg-gray-100 text-gray-700 font-medium hover:bg-gray-200"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="save-post-btn"
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-[#A8D8EA] to-[#7DBDD9] text-white font-bold shadow disabled:opacity-60"
                  >
                    {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Guardando…</> : <><Save className="w-4 h-4" /> Guardar</>}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
